import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MODE_LIST, MODE_SEARCH, PlanPickerContext } from './usePlanPickerContext';
import { normalizeItem } from '../usePlanPickerApi/items';
import usePlanPickerSideEffects from '../usePlanPickerSideEffects';

const noop = () => {};

/**
 * @param {SyncPlanPickerProviderProps} props
 */
function SyncPlanPickerProvider({ action, dataStrategy, initialIndex, items, children }) {
    const allItems = useMemo(
        () => items.filter(item => item.id !== '').map(item => normalizeItem(item)),
        [items]
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [value, setValue] = useState(allItems[initialIndex]);
    const runSideEffectsOnChange = usePlanPickerSideEffects({ action });
    const selectRef = useRef(null);

    /** @type {(selectedItem: import('../usePlanPickerApi/items').PlanPickerItem) => void} */
    const onChange = useCallback(
        selectedItem => {
            setValue(selectedItem);
            runSideEffectsOnChange(selectedItem);
        },
        [runSideEffectsOnChange]
    );

    /** @type {import("./usePlanPickerContext").PlanPickerContextBag} */
    const contextValue = useMemo(() => {
        const total = allItems.length;
        const normalizedSearchQuery = searchQuery.toLowerCase().trim();
        const mode = normalizedSearchQuery ? MODE_SEARCH : MODE_LIST;
        const options =
            mode === MODE_SEARCH
                ? allItems.filter(option => {
                      return option.name.toLowerCase().includes(normalizedSearchQuery);
                  })
                : allItems;

        return {
            dataStrategy,
            mode,
            options,
            total,
            isWaitingForInitialData: false,
            loadOptionsByIndex: noop,
            searchQuery,
            updateSearchQuery: setSearchQuery,
            selectedId: value?.id ?? null,
            value,
            onChange,
            selectRef
        };
    }, [allItems, dataStrategy, onChange, searchQuery, value]);

    return <PlanPickerContext.Provider value={contextValue}>{children}</PlanPickerContext.Provider>;
}

SyncPlanPickerProvider.propTypes = {
    dataStrategy: PropTypes.oneOf(['sync']),
    action: PropTypes.object,
    initialIndex: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object),
    children: PropTypes.node
};

export default SyncPlanPickerProvider;

/**
 * @typedef {object} SyncPlanPickerProviderProps
 * @prop {'sync'} dataStrategy
 * @prop {import('../usePlanPickerSideEffects').PlanChangeAction} action
 * @prop {string} initialId
 * @prop {number} initialIndex
 * @prop {import('../usePlanPickerApi/items').RawPlanPickerItem[]} items
 * @prop {import('react').ReactNode} [children]
 */
